import { activeCaseFilterIds } from "@/components/Forms/schema/activeCaseFilterIds.ts";
import { dtMetaSchema } from "@/components/Forms/schema/dtMetaSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createResourceCollectionSelector } from "jsonapi-zod-query";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";
import type { CaseFilter } from "./useFetchCaseFiltersets";

export const activeCaseFilterIdsSelector = createResourceCollectionSelector({
    type: "caseIds",
    attributesSchema: activeCaseFilterIds,
    documentMetaSchema: dtMetaSchema,
});

export type CaseIdsDocument = ReturnType<typeof activeCaseFilterIdsSelector>;
export type CaseId = CaseIdsDocument["data"][number];

type Props = {
    isQueryEnabled?: boolean;
    selectedTab?: string;
    taskDashboard?: boolean;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    activeFilter: CaseFilter | null;
    pagination: MRT_PaginationState;
    sorting: MRT_SortingState;
};

export const useFetchCaseIdsForActiveFilter = ({
    isQueryEnabled = true,
    selectedTab = "",
    taskDashboard = true,
    columnFilters,
    globalFilter,
    activeFilter,
    pagination,
    sorting,
}: Props): UseQueryResult<CaseIdsDocument> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: [
            "bulk-assignment-ids",
            columnFilters,
            globalFilter,
            pagination.pageIndex,
            pagination.pageSize,
            selectedTab,
            taskDashboard,
            sorting,
        ],
        enabled: isQueryEnabled,
        queryFn: async () => {
            const requestBody = {
                filters: columnFilters ?? [],
                globalFilter: globalFilter ?? "",
                activeFilter: activeFilter,
                sorting: sorting ?? [],
                selectedTab: selectedTab,
                taskDashboard: taskDashboard,
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
            };

            const response = await fetch(apiUrl("/dashboard/case-ids"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            return await response.json();
        },
        select: activeCaseFilterIdsSelector,
    });
};
